import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { inject, Signal } from '@angular/core';
import { CatalogFile, PermissionDiffValidateResponse, RoleDiffValidateResponse } from '@models/authorization';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { NotificationsService } from 'cui-components';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import { createStateResetHandler } from '../../helpers';
import { ImportChangesService } from './import-changes.service';

type ImportChangesState = {
  validationResult: PermissionDiffValidateResponse | RoleDiffValidateResponse | null;
  fileName: string | null;
  catalog: CatalogFile | null;
  isLoading: boolean;
};

const initialState: ImportChangesState = {
  validationResult: null,
  fileName: null,
  catalog: null,
  isLoading: false,
};

export const ImportChangesStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('import-changes-store'),

  withMethods((store, importChangesService = inject(ImportChangesService), notificationsService = inject(NotificationsService)) => {
    createStateResetHandler<ImportChangesState>('/import-changes')(store, initialState);

    const repositoryMethods = {
      validate: rxMethod<{ fileName: string; catalog: CatalogFile }>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap(({ fileName, catalog }) =>
            importChangesService.validate(catalog).pipe(
              tap(validationResult => {
                patchState(store, { validationResult, fileName, catalog, isLoading: false });
                if (validationResult.status === 'NO_CHANGES') {
                  notificationsService.showNotification('No changes were found in the uploaded file.');
                }
              }),
              catchError(() => {
                patchState(store, { isLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      refresh: rxMethod<void>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap(() => {
            const catalog = store.catalog();
            if (!catalog) {
              patchState(store, { isLoading: false });
              return EMPTY;
            }

            return importChangesService.validate(catalog).pipe(
              tap(validationResult => {
                patchState(store, { validationResult, isLoading: false });
                if (validationResult.status === 'NO_CHANGES') {
                  notificationsService.showNotification('No changes were found in the uploaded file.');
                }
              }),
              catchError(() => {
                patchState(store, { isLoading: false });
                return EMPTY;
              }),
            );
          }),
        ),
      ),

      save: rxMethod<void>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap(() => {
            const importId = store.validationResult()?.importId;
            if (!importId) {
              patchState(store, { isLoading: false });
              return EMPTY;
            }

            return importChangesService.approve(importId).pipe(
              tap(() => {
                patchState(store, initialState);
                notificationsService.showNotification('Authorization changes have been applied successfully.');
              }),
              catchError(() => {
                patchState(store, { isLoading: false });
                return EMPTY;
              }),
            );
          }),
        ),
      ),

      clear: (): void => {
        patchState(store, initialState);
      },
    };

    return {
      ...repositoryMethods,
    };
  }),
);

export interface ImportChangesStoreType {
  readonly validationResult: Signal<PermissionDiffValidateResponse | RoleDiffValidateResponse | null>;
  readonly fileName: Signal<string | null>;
  readonly catalog: Signal<CatalogFile | null>;
  readonly isLoading: Signal<boolean>;

  readonly validate: (params: { fileName: string; catalog: CatalogFile }) => void;
  readonly refresh: () => void;
  readonly save: () => void;
  readonly clear: () => void;
}
